import { useMemo } from 'react'

import { isBefore, isFuture } from 'date-fns'

import { Appointment, Provider } from '../../../models'
import { getDateFromAppointment } from '../../appointments/getDateFromAppointment'
import { getProviderSessionCounts } from '../utils'

const getUpcomingProviderAppts = (providers: Provider[]): UpcomingProviderAppointments =>
  providers?.reduce((upcomingAppts, provider) => {
    provider.appointments.forEach((appointment) => {
      const isUpcoming =
        appointment.sessionNumber ===
          getProviderSessionCounts(appointment.lyraProviderId, appointment.episodeId, providers) &&
        isFuture(getDateFromAppointment(appointment))

      if (isUpcoming && ['newAppt', 'rescheduled'].includes(appointment.appointmentStatus)) {
        if (appointment.episodeId) {
          const existingAppointment = upcomingAppts[appointment.episodeId]
          // Keep the earliest upcoming appointment for each episode
          if (
            !existingAppointment ||
            isBefore(getDateFromAppointment(appointment), getDateFromAppointment(existingAppointment))
          ) {
            upcomingAppts[appointment.episodeId] = appointment
          }
        }
      }
    })
    return upcomingAppts
  }, {} as UpcomingProviderAppointments)

/**
 * Gets the closest upcoming appointment for each episode across all providers.
 * Keyed by episodeId to properly distinguish between different patients' appointments
 * with the same provider (e.g., parent and child episodes with the same therapist).
 */
export const useGetUpcomingProviderAppts = ({
  providers,
}: UseGetUpcomingProviderApptsParams): UpcomingProviderAppointments => {
  return useMemo(() => (providers?.length ? getUpcomingProviderAppts(providers) : {}), [providers])
}

type UseGetUpcomingProviderApptsParams = {
  providers: Provider[]
}

export type UpcomingProviderAppointments = { [episodeId: string]: Appointment }
